import { useMutation } from "@apollo/client/react";
import { readFragment, type VariablesOf } from "gql.tada";
import { useStore } from "@/app/store";
import { SAVE_INPUTS_AND_EXECUTE } from "../graphql/mutations";
import { CALCULATION_DETAIL_FRAGMENT } from "../graphql/queries";
import useOpDataNotPopulatedHandler from "./useOpDataNotPopulatedHandler";
import useStaleVersionHandler from "./useStaleVersionHandler";

type SaveInputsAndExecuteVariables = VariablesOf<
  typeof SAVE_INPUTS_AND_EXECUTE
>;

export type SaveInputsAndExecuteCallerVariables = {
  input: Omit<SaveInputsAndExecuteVariables["input"], "expectedVersion">;
};

interface SaveInputsAndExecuteCallbacks {
  onSuccess?: () => void;
  onError?: () => void;
}

const useSaveInputsAndExecute = (
  onConflict: () => void,
  { onSuccess, onError }: SaveInputsAndExecuteCallbacks = {},
) => {
  const handleStale = useStaleVersionHandler(onConflict);
  const handleOpDataNotPopulated = useOpDataNotPopulatedHandler();
  const setCalculation = useStore((s) => s.setCalculation);
  const [runMutation, { loading }] = useMutation(SAVE_INPUTS_AND_EXECUTE);

  const saveInputsAndExecute = async (
    variables: SaveInputsAndExecuteCallerVariables,
  ) => {
    const { calculation } = useStore.getState();
    if (!calculation) return;
    const calledForId = calculation.id;
    const result = await runMutation({
      variables: {
        input: { ...variables.input, expectedVersion: calculation.version },
      },
    });
    if (handleOpDataNotPopulated(result.error)) return;
    if (handleStale(result.error)) return;
    if (result.error) {
      onError?.();
      return;
    }
    if (result.data?.saveInputsAndExecute) {
      setCalculation(
        readFragment(
          CALCULATION_DETAIL_FRAGMENT,
          result.data.saveInputsAndExecute,
        ),
        calledForId,
      );
      onSuccess?.();
    }
  };

  return { saveInputsAndExecute, loading };
};

export default useSaveInputsAndExecute;
